import React from 'react';

import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useGetStatementFileQuery } from '../api';
import AnalysisView from '../components/AnalysisView';
import Loader from '../components/Loader';
import { useOnboarding } from '../hooks/useOnboarding';
import { useAuth } from '../providers/AppAuthProvider';

// Transform StatementFile to Document interface for backward compatibility
interface Document {
	id: string;
	name: string;
	type: string;
	date: string;
	size: string;
	status: string;
	processingMonth: string;
	pdfUrl?: string;
	results?: any;
	uploaded_by?: { name: string; email: string };
}

const transformToDocument = (statement: any): Document => {
	return {
		id: String(statement.id),
		name: statement.original_filename,
		type: statement.analysis_type,
		date: new Date(statement.created_at).toISOString().split('T')[0],
		size: `${(statement.file_size / 1024).toFixed(1)} KB`,
		status:
			statement.current_status === 'processed'
				? 'completed'
				: statement.current_status === 'processing'
					? 'processing'
					: statement.current_status === 'error'
						? 'failed'
						: statement.current_status,
		processingMonth: new Date(statement.created_at).toLocaleDateString(
			'en-US',
			{ month: 'long', year: 'numeric' },
		),
		pdfUrl: statement.results?.signed_urls?.pdf_url,
		results: statement.results,
		uploaded_by: statement.uploaded_by,
	};
};

const AnalysisViewPage: React.FC = () => {
	const { fileId } = useParams<{ fileId: string }>();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { user } = useAuth();
	const { completeStepById, nextStep } = useOnboarding();

	// Check if we're in onboarding context based on route path
	const isOnboardingContext =
		window.location.pathname.startsWith('/onboarding');

	// Fetch the single statement by its URL id. Previously this filtered the
	// paginated list (newest 50) client-side, so deep-linking or refreshing a
	// statement outside that page returned `undefined` -> blank screen.
	const {
		data: statement,
		isLoading,
		error,
		refetch,
	} = useGetStatementFileQuery(fileId ?? '', { skip: !fileId });

	const selectedDocument = statement ? transformToDocument(statement) : null;

	const handleBack = () => {
		if (isOnboardingContext) {
			navigate('/onboarding');
		} else {
			navigate('/');
		}
	};

	const handleOnboardingComplete = () => {
		if (isOnboardingContext) {
			// Mark step 1 (Upload Statement) as completed
			completeStepById(1);
			navigate('/onboarding');
		} else {
			// Not in onboarding context, just go back
			navigate('/');
		}
	};

	const handleRefresh = () => {
		refetch();
	};

	if (isLoading) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
				<Loader size="xl" text="Loading analysis..." />
			</div>
		);
	}

	if (error) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
				<div className="text-center">
					<div className="text-red-600 text-lg mb-4">
						Error loading analysis
					</div>
					<p className="text-gray-600 mb-4">Please try again later.</p>
					<button
						onClick={() => refetch()}
						className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
					>
						Retry
					</button>
				</div>
			</div>
		);
	}

	if (!selectedDocument) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
				<div className="text-center">
					<div className="text-gray-800 text-lg mb-2">
						Analysis not found
					</div>
					<p className="text-gray-600 mb-4">
						This statement may have been deleted or you don&apos;t have
						access to it.
					</p>
					<button
						onClick={handleBack}
						className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
					>
						Back to Reports
					</button>
				</div>
			</div>
		);
	}

	return (
		<AnalysisView
			document={selectedDocument as Document}
			onBack={handleBack}
			onRefresh={handleRefresh}
			onboardingContext={isOnboardingContext}
			onOnboardingComplete={handleOnboardingComplete}
		/>
	);
};

export default AnalysisViewPage;
